
import { Calendar, EventClickArg } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { defineComponent } from 'vue'
import AppointmentService from '../common/appointmentService'
import { endOfWeek, format, startOfWeek } from 'date-fns'
import AppointmentScheduler from '../components/AppointmentScheduler.vue'
import Appointment from '../models/appointment'
import { AppointmentType } from '../models/appointmentType'
import User from '../models/user'
import Litepicker from 'litepicker'
import UserService from '../common/userService'
import AppointmentTypeService from '../common/appointmentTypeService'
import AppointmentInfoModal from '../components/AppointmentModal.vue'
import { mapGetters, mapState } from 'vuex'

export default defineComponent({
  name: 'Calendar',

  data () {
    return {
      /** AppointmentType Filters */
      appointmentTypeFilters: {} as { [id: string]: boolean },

      /** List of appointment types */
      appointmentTypes: [] as Array<AppointmentType>,

      calendarInitialized: false,

      events: [] as Array<any>,

      /** The fullcalendar object */
      calendar: null as unknown as Calendar,

      /** The current date of the calendar */
      currentDate: new Date(),

      /** Reviewer filters */
      reviewerFilters: {} as { [id: string]: boolean },

      /** List of users who are reviewers */
      reviewers: [] as Array<User>,

      /** The appointment to show info for */
      selectedAppoiontment: null as unknown as Appointment,

      /** Shows or hides the appointment info modal */
      shouldShowAppointment: false,

      /** Shows or hides the filters pane */
      shouldShowFilters: false,

      /** Shows or hides the schedule form */
      shouldShowScheduleForm: false,

      /** Shows or hides the task pane */
      shouldShowTasks: false,

      /** Shows or hides the reviewer filters */
      showReviewerFilters: true,

      /** Shows or hides the appointment type filters */
      showAppointmentTypeFilters: true,

      showOutlookEvents: true,

      /** Shows or hides the appointment type filters */
      showOutlookEventFilters: true,

      /** The view mode of the calendar */
      viewMode: 'timeGridWeek'
    }
  },

  computed: {
    /**
     * Format calendar header based on selection month, week, or day
     */
    calendarHeader (): string {
      switch (this.viewMode) {
        case 'timeGridDay': return format(this.currentDate, 'MMM do yyyy')
        case 'timeGridWeek': return `${format(startOfWeek(this.currentDate), 'MMM do')} - ${format(endOfWeek(this.currentDate), 'MMM do')}`
        default: return format(this.currentDate, 'MMMM yyyy')
      }
    },

    ...mapState(['userId']),
    ...mapGetters(['isAdmin', 'isReviewer', 'isReadOnly'])
  },

  mounted () {
    // get reviewers and thier appoitnment type lists
    this.getReviewers().then(() => {
      this.getAppointmentTypes().then(() => {
        this.initializeCalendar()
      })
    })

    // calendare setup
    setTimeout(() => {
      const picker = new Litepicker({ element: document.querySelector('.mini-cal'), inlineMode: true, singleMode: true, onSelect: this.handleMiniCalSelection, disableWeekends: true })
      picker.show()
    }, 0)
  },

  watch: {
    appointmentTypeFilters: {
      handler () {
        this.rerender()
      },
      deep: true
    },

    /**
     * show Outlook events
     */
    showOutlookEvents: function () {
      this.rerender()
    },

    reviewerFilters: {
      handler () {
        this.rerender()
      },
      deep: true
    },

    /**
     * show or hide filters
     */
    shouldShowFilters: function () {
      this.calendar.render()
    },

    /**
     * show or hide tasks
     */
    shouldShowTasks: function () {
      this.calendar.render()
    },

    /**
     * calendar view mode
     */
    viewMode: function (val) {
      this.calendar.changeView(val)
    }
  },

  methods: {
    /**
     * remove appointment
     */
    removeAppointment (id) {
      const event = this.calendar.getEventById(String(id))
      if (event) {
        event.remove()
      }
    },

    /**
     * Get reviewers
     */
    getReviewers (): Promise<boolean> {
      return new Promise(resolve => {
        UserService.getOspStaff().then(res => {
          this.reviewers = res.data.filter(u => u.permissions.find(p => p.code === 'REVU') != null)

          this.reviewers.forEach(r => {
            this.reviewerFilters[r.id] = (!(this.isAdmin || this.isReadOnly) && this.isReviewer) ? r.id === this.userId : true
          })

          resolve(true)
        })
      })
    },

    /**
     * Get appointment types
     */
    getAppointmentTypes (): Promise<boolean> {
      return new Promise(resolve => {
        AppointmentTypeService.getAll().then(res => {
          this.appointmentTypes = res.data
          this.appointmentTypes.forEach(at => {
            this.appointmentTypeFilters[at.id] = true
          })
          resolve(true)
        })
      })
    },

    /**
     * handle changing start for calendar
     */
    handleMiniCalSelection (start) {
      this.calendar.gotoDate(start)
    },

    /**
     * handle rescheduling appointment
     */
    handleReschedule (appointment: Appointment) {
      const st = this.calendar.currentData.viewApi.activeStart
      const et = this.calendar.currentData.viewApi.activeEnd
      this.fetchEvents({ start: st, end: et })
      // const event = this.calendar.getEventById(String(appointment.id))
      // if (event) {
      //   event.remove()
      //   const calEvent = {
      //     start: appointment.start,
      //     end: appointment.end,
      //     appointment: appointment,
      //     type: 'Osp',
      //     allDay: appointment.appointmentType.isTask
      //   }

      //   const fcEvent = this.toFcEvent(calEvent)
      //   this.calendar.addEvent(fcEvent)
      // }

      this.selectedAppoiontment = appointment
    },

    /**
     * rebuild calendar view
     */
    rerender () {
      if (!this.calendarInitialized) {
        return
      }
      this.calendar.batchRendering(() => {
        this.calendar.removeAllEvents()
      })
      this.calendar.batchRendering(() => {
        this.events.forEach(e => {
          if (this.filterEvents(e)) {
            this.calendar.addEvent(e)
          }
        })
      })
    },

    handleUpdated (appointment: Appointment) {
      const event = this.calendar.getEventById(String(appointment.id))
      if (event) {
        event.setExtendedProp('proposalNumber', appointment.proposalNumber)
        event.setExtendedProp('piFirstName', appointment.piFirstName)
        event.setExtendedProp('piLastName', appointment.piLastName)
        event.setExtendedProp('piPhone', appointment.piPhone)
        event.setExtendedProp('piEmail', appointment.piEmail)
        event.setExtendedProp('additionalContacts', appointment.additionalContacts)
        event.setExtendedProp('intakeFormResponse', appointment.intakeFormResponse)
        event.setExtendedProp('proposalNumberType', appointment.proposalNumberType)

        this.selectedAppoiontment = event.extendedProps as Appointment
      }
    },

    /**
     * handle reassigning appointment
     */
    handleReassign (appointment: Appointment) {
      const event = this.calendar.getEventById(String(appointment.id))
      if (event) {
        event.setExtendedProp('reviewer', appointment.reviewer)
      }

      this.shouldShowAppointment = false
    },

    /**
     * handle scheduling an appointment
     */
    handleScheduled () {
      setTimeout(() => {
        this.shouldShowScheduleForm = false
        const st = this.calendar.currentData.viewApi.activeStart
        const et = this.calendar.currentData.viewApi.activeEnd
        this.fetchEvents({ start: st, end: et })
      }, 1000)
    },

    /**
     * filter view
     */
    filterEvents (event): boolean {
      if (event.extendedProps.type === 'Osp') {
        const ospEvent = event.extendedProps as Appointment

        if (this.reviewerFilters[ospEvent.reviewer.id] === false) {
          return false
        }

        if (this.appointmentTypeFilters[ospEvent.appointmentType.id] === false) {
          return false
        }
      } else if (event.extendedProps.type === 'External') {
        if (!this.showOutlookEvents) {
          return false
        }

        if (this.reviewerFilters[event.extendedProps.reviewer.id] === false) {
          return false
        }
      } else if (event.extendedProps.type === 'BlockedTime') {
        if (this.reviewerFilters[event.extendedProps.user.id] === false) {
          return false
        }
      }

      return true
    },

    /**
     * Construct an event
     */
    constructEvent (arg) {
      if (arg.event.extendedProps.type === 'Osp') {
        const ospEvent = arg.event.extendedProps as Appointment

        const el = document.createElement('div')
        el.classList.add('osp-event')
        el.style.background = ospEvent.appointmentType.color + '33'
        el.style.color = ospEvent.appointmentType.color
        el.style.borderColor = ospEvent.appointmentType.color

        const nameEl = document.createElement('div')
        nameEl.classList.add('pi-name')
        nameEl.innerHTML = `${ospEvent.piFirstName} ${ospEvent.piLastName}`
        el.appendChild(nameEl)

        const cayuse = document.createElement('div')
        cayuse.classList.add('cayuse-num')

        let type: string
        switch (ospEvent.proposalNumberType) {
          case 'ProposalDevelopment': type = 'PD'; break
          case 'QuickProposal': type = 'QP'; break
          default: type = 'Cayuse'
        }

        cayuse.innerHTML = `${type}: ${ospEvent.proposalNumber}`
        el.appendChild(cayuse)

        if (this.isAdmin) {
          const reviewerEl = document.createElement('div')
          reviewerEl.classList.add('reviewer-info')
          reviewerEl.innerHTML = `Reviewer: ${ospEvent.reviewer.firstName} ${ospEvent.reviewer.lastName}`
          el.appendChild(reviewerEl)
        }

        return { domNodes: [el] }
      } else if (arg.event.extendedProps.type === 'External') {
        const el = document.createElement('div')
        el.classList.add('external-event')
        if (arg.event.extendedProps.busy) {
          el.classList.add('busy')
        }

        const titleEl = document.createElement('div')
        titleEl.classList.add('title')
        titleEl.innerHTML = arg.event.extendedProps.title ? arg.event.extendedProps.title : 'External Event'
        el.appendChild(titleEl)

        const reviewerEl = document.createElement('div')
        reviewerEl.classList.add('reviewer-name')
        reviewerEl.innerHTML = `${arg.event.extendedProps.reviewer.firstName} ${arg.event.extendedProps.reviewer.lastName}`
        el.appendChild(reviewerEl)

        return { domNodes: [el] }
      } else if (arg.event.extendedProps.type === 'BlockedTime') {
        const el = document.createElement('div')
        el.classList.add('blocked-time')
        el.innerHTML = `Blocked Time for ${arg.event.extendedProps.user.firstName} ${arg.event.extendedProps.user.lastName} : ${arg.event.extendedProps.title}`

        return { domNodes: [el] }
      }
    },

    /**
     * Creates the fullcalendar object and renders the calendar
     */
    initializeCalendar () {
      const calendarElement = document.querySelector('.fullcalendar') as HTMLElement
      this.calendar = new Calendar(calendarElement, {
        allDayText: 'All Day',
        height: '100%',
        weekends: false,
        allDaySlot: true,
        editable: false,
        headerToolbar: false,
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        slotDuration: '00:30:00',
        slotMinTime: '06:00:00',
        slotMaxTime: '18:00:00',
        slotEventOverlap: false,
        eventClick: this.handleEventClick,
        datesSet: (arg) => {
          this.fetchEvents(arg)
          this.currentDate = this.calendar.getDate()
        },
        eventContent: this.constructEvent
      })

      // if (this.isAdmin) {
      //   this.viewMode = 'timeGridWeek'
      // }

      this.calendar.changeView(this.viewMode)

      this.calendar.render()
    },

    /**
     * Get events for given date range
     */
    fetchEvents (dates) {
      AppointmentService.getAll(dates.start, dates.end).then(res => {
        const events = [] as Array<any>
        this.events = []
        res.data.forEach((event: any) => {
          const fcEvent = this.toFcEvent(event)

          this.events.push(fcEvent)

          if (this.filterEvents(fcEvent)) {
            events.push(fcEvent)
          }
        })
        this.calendarInitialized = true
        this.rerender()
      })
    },

    /**
     * format date time
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * handle the click event
     */
    handleEventClick (eventInfo: EventClickArg) {
      if (eventInfo.event.extendedProps.type !== 'Osp') {
        return
      }

      this.selectedAppoiontment = eventInfo.event.extendedProps as Appointment
      this.shouldShowAppointment = true
    },

    /**
     * To FC event
     */
    toFcEvent (app: any): object {
      const fcEvent = {
        start: new Date(app.start),
        end: new Date(app.end),
        allDay: app.allDay
      } as any

      if (app.type === 'Osp') {
        app.appointment.start = new Date(app.appointment.start)
        app.appointment.end = new Date(app.appointment.end)
        app.appointment.reviewDeadline = new Date(app.appointment.reviewDeadline)
        app.appointment.scheduledOn = new Date(app.appointment.scheduledOn)
        fcEvent.extendedProps = app.appointment
        fcEvent.extendedProps.type = 'Osp'
        fcEvent.id = app.appointment.id
      } else if (app.type === 'External') {
        app.externalAppointment.start = new Date(app.externalAppointment.start)
        app.externalAppointment.end = new Date(app.externalAppointment.end)
        fcEvent.extendedProps = app.externalAppointment
        fcEvent.extendedProps.type = 'External'
      } else if (app.type === 'BlockedTime') {
        app.blockedTime.startTime = new Date(app.blockedTime.startTime)
        app.blockedTime.endTime = new Date(app.blockedTime.endTime)
        fcEvent.extendedProps = app.blockedTime
        fcEvent.extendedProps.type = 'BlockedTime'
      }

      return fcEvent
    },

    viewRendered (what) {
      console.log('what')
    },

    /**
     * get next calendar period
     */
    next () {
      this.calendar.next()
    },

    /**
     * get previous calendar period
     */
    prev () {
      this.calendar.prev()
    }
  },

  components: {
    AppointmentScheduler,
    AppointmentInfoModal
  }
})
